import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { resolveMobileTabs } from "@/lib/kiosk";
import { APP_VERSION, APP_BUILD } from "@/version";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut, LayoutDashboard, SlidersHorizontal } from "lucide-react";
import { ERP_MODULES } from "./erp/erpShared";
import ErpDashboard from "./erp/ErpDashboard";
import ModulesBeheer from "./erp/ModulesBeheer";
import InkoopTab from "./erp/InkoopTab";
import WerkordersTab from "./erp/WerkordersTab";
import KwaliteitTab from "./erp/KwaliteitTab";
import CrediteurenTab from "./erp/CrediteurenTab";
import TraceerbaarheidTab from "./erp/TraceerbaarheidTab";
import RapportageTab from "./erp/RapportageTab";
import OnderhoudTab from "./erp/OnderhoudTab";
import HrmTab from "./erp/HrmTab";
import FacturenTab from "./sales/FacturenTab";

const isMgmt = (u) => ["Bedrijfsleiding", "Bedrijfsleiding 2"].includes(u?.role) || ["Bedrijfsleiding", "Bedrijfsleiding 2"].includes(u?.roleBase);

export default function ERPApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { const mt = resolveMobileTabs(user?.role, null); if (mt && mt.length && user?.role !== "Bedrijfsleiding") navigate("/mobiel", { replace: true }); /* eslint-disable-next-line */ }, [user?.role]);
  const canManage = isMgmt(user);
  const [flags, setFlags] = useState({});
  const [tab, setTab] = useState("dashboard");
  const [klanten, setKlanten] = useState([]);
  const [artikelen, setArtikelen] = useState([]);
  const [orders, setOrders] = useState([]);
  const [facturen, setFacturen] = useState([]);
  const [inkooporders, setInkooporders] = useState([]);
  const [werkorders, setWerkorders] = useState([]);
  const [kwaliteit, setKwaliteit] = useState([]);
  const [crediteuren, setCrediteuren] = useState([]);
  const [recalls, setRecalls] = useState([]);
  const [onderhoud, setOnderhoud] = useState([]);
  const [appd, setAppd] = useState({ expeditie: [], entries: [], halffabRuns: [], products: [], personeel: [], vakanties: [], personeelUren: [], planning: [] });

  const loadFlags = () => api.get("/erp-modules").then((r) => setFlags(r.data.modules || {})).catch(() => {});
  const loadAll = () => {
    api.get("/sales/klanten").then((r) => setKlanten(r.data)).catch(() => {});
    api.get("/sales/artikelen").then((r) => setArtikelen(r.data.artikelen || [])).catch(() => {});
    api.get("/sales/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/sales/facturen").then((r) => setFacturen(r.data)).catch(() => {});
    api.get("/erp/inkooporders").then((r) => setInkooporders(r.data)).catch(() => {});
    api.get("/erp/werkorders").then((r) => setWerkorders(r.data)).catch(() => {});
    api.get("/erp/kwaliteit").then((r) => setKwaliteit(r.data)).catch(() => {});
    api.get("/erp/crediteuren").then((r) => setCrediteuren(r.data)).catch(() => {});
    api.get("/erp/recalls").then((r) => setRecalls(r.data)).catch(() => {});
    api.get("/erp/onderhoud").then((r) => setOnderhoud(r.data)).catch(() => {});
    api.get("/appdata").then((r) => { const d = (r.data && r.data.data) || {}; setAppd({ expeditie: d.expeditie || [], entries: d.entries || [], halffabRuns: d.halffabRuns || [], products: d.products || [], personeel: d.personeel || [], vakanties: d.vakanties || [], personeelUren: d.personeelUren || [], planning: d.planning || [] }); }).catch(() => {});
  };
  useEffect(() => { loadFlags(); loadAll(); }, []);

  if (!canManage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center">
        <div><p className="text-lg font-semibold text-slate-700">Geen toegang tot ERP</p><Button className="mt-4" onClick={() => navigate("/")}>Terug naar app</Button></div>
      </div>
    );
  }

  const on = (k) => !!flags[k];
  const activeModules = ERP_MODULES.filter((m) => m.built && on(m.key));

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <img src="/clazing-logo.png" alt="Clazing" className="h-9 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div>
            <div className="text-base font-bold text-slate-900 leading-tight">Clazing ERP</div>
            <div className="text-[11px] text-slate-400">v{APP_VERSION} · {APP_BUILD}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
            <div className="text-[11px] font-bold text-blue-700">{user?.roleLabel || user?.role}</div>
          </div>
          <Button variant="outline" size="sm" data-testid="erp-to-prod" onClick={() => navigate("/")}>Productie-app</Button>
          <Button variant="ghost" size="sm" data-testid="erp-logout" onClick={() => { logout(); navigate("/login"); }}><LogOut className="h-4 w-4 mr-1" /> Uitloggen</Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl p-4 sm:p-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="erp-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="dashboard" data-testid="erp-tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1" /> Overzicht</TabsTrigger>
            {activeModules.map((m) => { const Icon = m.Icon; return <TabsTrigger key={m.key} value={m.key} data-testid={`erp-tab-${m.key}`}><Icon className="h-4 w-4 mr-1" /> {m.label}</TabsTrigger>; })}
            <TabsTrigger value="modules" data-testid="erp-tab-modules"><SlidersHorizontal className="h-4 w-4 mr-1" /> Modules</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-4">
            <ErpDashboard flags={flags} facturen={facturen} inkooporders={inkooporders} setTab={setTab} />
          </TabsContent>
          {on("facturen") && (
            <TabsContent value="facturen" className="mt-4">
              <FacturenTab klanten={klanten} artikelen={artikelen} facturen={facturen} orders={orders} reload={loadAll} />
            </TabsContent>
          )}
          {on("inkoop") && (
            <TabsContent value="inkoop" className="mt-4">
              <InkoopTab artikelen={artikelen} inkooporders={inkooporders} reload={loadAll} />
            </TabsContent>
          )}
          {on("werkorders") && (
            <TabsContent value="werkorders" className="mt-4">
              <WerkordersTab artikelen={artikelen} werkorders={werkorders} planning={appd.planning} reload={loadAll} />
            </TabsContent>
          )}
          {on("kwaliteit") && (
            <TabsContent value="kwaliteit" className="mt-4">
              <KwaliteitTab kwaliteit={kwaliteit} reload={loadAll} />
            </TabsContent>
          )}
          {on("crediteuren") && (
            <TabsContent value="crediteuren" className="mt-4">
              <CrediteurenTab crediteuren={crediteuren} inkooporders={inkooporders} reload={loadAll} />
            </TabsContent>
          )}
          {on("traceerbaarheid") && (
            <TabsContent value="traceerbaarheid" className="mt-4">
              <TraceerbaarheidTab expeditie={appd.expeditie} entries={appd.entries} halffabRuns={appd.halffabRuns} orders={orders} recalls={recalls} reload={loadAll} />
            </TabsContent>
          )}
          {on("rapportage") && (
            <TabsContent value="rapportage" className="mt-4">
              <RapportageTab facturen={facturen} crediteuren={crediteuren} orders={orders} appd={appd} />
            </TabsContent>
          )}
          {on("onderhoud") && (
            <TabsContent value="onderhoud" className="mt-4">
              <OnderhoudTab onderhoud={onderhoud} reload={loadAll} />
            </TabsContent>
          )}
          {on("hrm") && (
            <TabsContent value="hrm" className="mt-4">
              <HrmTab personeel={appd.personeel} vakanties={appd.vakanties} personeelUren={appd.personeelUren} />
            </TabsContent>
          )}
          <TabsContent value="modules" className="mt-4">
            <ModulesBeheer flags={flags} reload={loadFlags} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
